import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  ProjectHeatmapClient,
  IProjectHeatmapClient,
  CreateHeatmapCommand,
  HeatmapSummaryDto,
} from "app/ApiClient";
import { LoadingState } from "types";
import { initialProjectHeatmapState, ProjectHeatmapState } from "./projectHeatmapState";

const projectSettingsClient: IProjectHeatmapClient = new ProjectHeatmapClient(
  process.env.REACT_APP_API_URL,
);

export const getProjectHeatmaps = createAsyncThunk(
  "projects/getProjectHeatmaps",
  async (projectId: number) => {
    const projectHeatmapSettings = await projectSettingsClient.getAllHeatmaps(projectId);
    return projectHeatmapSettings;
  },
);

export const createProjectHeatmap = createAsyncThunk(
  "projects/createProjectHeatmap",
  async ({ projectId, command }: { projectId: number; command: CreateHeatmapCommand }) => {
    const newHeatmap = await projectSettingsClient.createHeatmap(projectId, command);
    return newHeatmap;
  });

export const updateProjectHeatmapName = createAsyncThunk(
  "projects/updateProjectHeatmapName",
  async ({ projectId, heatmapId, newName }: { projectId: number; heatmapId: number; newName: string }) => {
    const updatedHeatmap = await projectSettingsClient.renameHeatmap(projectId, heatmapId, { name: newName });
    return updatedHeatmap;
  });

export const deleteProjectHeatmap = createAsyncThunk(
  "projects/deleteProjectHeatmap",
  async ({ projectId, heatmapId }: { projectId: number; heatmapId: number }) => {
    await projectSettingsClient.deleteHeatmap(projectId, heatmapId);
    return heatmapId;
  });

const applyFilters = (
  all: HeatmapSummaryDto[],
  filters: ProjectHeatmapState["filters"],
): HeatmapSummaryDto[] => {
  const activeFilters = Object.entries(filters).filter(([, v]) => Array.isArray(v) && v.length > 0);
  if (!activeFilters.length) return all;
  return all.filter((item) =>
    activeFilters.every(([key, values]) => {
      const fieldValue = item[key as keyof HeatmapSummaryDto];
      const actual = fieldValue == undefined ? "" : String(fieldValue);
      return values.includes(actual);
    }),
  );
};

const projectHeatmapSlice = createSlice({
  name: "projectHeatmap",
  initialState: initialProjectHeatmapState,
  reducers: {
    resetProjectHeatmapSettings: () => initialProjectHeatmapState,
    setHeatmapFilters: (state, action: PayloadAction<Partial<Record<keyof HeatmapSummaryDto, string[]>>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.projectHeatmapSettings = applyFilters(state.allProjectHeatmapSettings, state.filters);
    },
    setEditedHeatmapName: (state, action: PayloadAction<{ heatmapID: number; newName: string }>) => {
      const { heatmapID, newName } = action.payload;
      const update = (list: HeatmapSummaryDto[]) => {
        const heatmap = list.find((h) => h.id === heatmapID);
        if (heatmap) heatmap.name = newName;
      };
      update(state.allProjectHeatmapSettings);
      update(state.projectHeatmapSettings);
    },
  },
  extraReducers: (builder) => {
    // getProjectHeatmaps
    builder.addCase(getProjectHeatmaps.pending, (state) => {
      state.loading = LoadingState.pending;
    });
    builder.addCase(getProjectHeatmaps.fulfilled, (state, { payload }) => {
      state.loading = LoadingState.succeed;
      state.allProjectHeatmapSettings = payload;
      state.projectHeatmapSettings = applyFilters(payload, state.filters);
    });
    builder.addCase(getProjectHeatmaps.rejected, (state) => {
      state.loading = LoadingState.failed;
    });
    // createProjectHeatmap
    builder.addCase(createProjectHeatmap.pending, (state) => {
      state.loading = LoadingState.pending;
    });
    builder.addCase(createProjectHeatmap.fulfilled, (state) => {
      state.loading = LoadingState.succeed;
    });
    builder.addCase(createProjectHeatmap.rejected, (state) => {
      state.loading = LoadingState.failed;
    });
    // deleteProjectHeatmap
    builder.addCase(deleteProjectHeatmap.pending, (state) => {
      state.loading = LoadingState.pending;
    });
    builder.addCase(deleteProjectHeatmap.fulfilled, (state, { payload: heatmapId }) => {
      state.loading = LoadingState.succeed;
      state.allProjectHeatmapSettings = state.allProjectHeatmapSettings.filter((h) => h.id !== heatmapId);
      state.projectHeatmapSettings = state.projectHeatmapSettings.filter((h) => h.id !== heatmapId);
    });
    builder.addCase(deleteProjectHeatmap.rejected, (state) => {
      state.loading = LoadingState.failed;
    });
  },
});

export const { resetProjectHeatmapSettings, setEditedHeatmapName, setHeatmapFilters } = projectHeatmapSlice.actions;
export default projectHeatmapSlice.reducer;
